// The driver's certifications under 49 CFR 391.21.
//
// Three things this section is careful about. It asks only the questions this
// entry actually raises — termination for a job that ended, contact permission
// for the one that has not. It counts what is answered, so the work left is
// visible without scrolling. And where an answer has already been given
// elsewhere it says so, rather than presenting it as a fresh certification.

import {
  answeredCount,
  canCopyAnswers,
  copyableAnswers,
  questionsFor,
} from '../lib/certifications'
import type { EntryDraft, Errors, FieldName } from '../lib/validation'
import { COMPLIANCE_QUESTIONS, TERMINATED_REASON } from '../values'

import { ComplianceQuestion } from './ComplianceQuestion'
import { SectionHeader } from './SectionHeader'

export interface ComplianceSectionProps {
  draft: EntryDraft
  onAnswer: (field: FieldName, value: boolean) => void
  /** The previous entry, offered as a source for the two portable answers. */
  copyFrom?: EntryDraft | null
  onCopy?: (answers: Partial<EntryDraft>) => void
  errors?: Errors
}

export const ComplianceSection = ({
  draft,
  onAnswer,
  copyFrom,
  onCopy,
  errors = {},
}: ComplianceSectionProps) => {
  const questions = questionsFor(draft)
  const { answered, total } = answeredCount(draft)
  const offerCopy =
    Boolean(onCopy) && Boolean(copyFrom) && canCopyAnswers(copyFrom, draft)

  // Saying "Terminated / discharged" is the answer to "were you terminated?".
  // The question still shows, so the driver can see and change what was
  // recorded, but it is labelled rather than asked twice as if unrelated.
  const terminatedFromReason =
    !draft.isCurrent &&
    draft.reasonForLeaving === TERMINATED_REASON &&
    draft.isDriverTerminated === true

  // A current job answers this by being current, so say so rather than leaving
  // an answer on screen that nobody appears to have given.
  const terminatedFromCurrent =
    Boolean(draft.isCurrent) && draft.isDriverTerminated === false

  return (
    <div className="rounded-[13px] border border-doubleNickel-gray-200 bg-white px-3 py-2.5">
      <SectionHeader
        title="Compliance"
        count={`${answered} of ${total} answered`}
        complete={answered === total}
      />

      {offerCopy && (
        <button
          type="button"
          onClick={() => onCopy?.(copyableAnswers(copyFrom))}
          className="mt-1.5 w-full rounded-lg border border-doubleNickel-gray-300 bg-white py-2 text-sm font-semibold text-doubleNickel-brand-700"
        >
          Same answers as {copyFrom?.companyName}
        </button>
      )}

      <div className="mt-1">
        {questions.map((field) => (
          <div key={field}>
            <ComplianceQuestion
              question={
                COMPLIANCE_QUESTIONS[field as keyof typeof COMPLIANCE_QUESTIONS]
              }
              value={draft[field as keyof EntryDraft] as boolean | null}
              onAnswer={(value) => onAnswer(field, value)}
              error={errors[field]}
            />
            {field === 'isDriverTerminated' && terminatedFromReason && (
              <div className="pb-2 text-[11px] text-doubleNickel-gray-500">
                From your reason for leaving. Change it here if that is wrong.
              </div>
            )}
            {field === 'isDriverTerminated' && terminatedFromCurrent && (
              <div className="pb-2 text-[11px] text-doubleNickel-gray-500">
                Because you still work here.
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
